import {Component, EventEmitter, Inject, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Master} from '../../../../admin/models/master.interface';
import {QuestionsService} from '../../../services/questions.service';
import {Questions} from '../../../models/questions.interface';

@Component({
  selector: 'app-create-questions',
  templateUrl: './create-questions.component.html',
  styleUrls: ['./create-questions.component.scss']
})
export class CreateQuestionsComponent implements OnInit {

  questionTypes = [
    {value: 'COUNTRY', description: 'Country'},
    {value: 'SYSTEM', description: 'System'},
    {value: 'BOTH', description: 'Both'},
  ];

  sharingLevel = [
    {value: 'UNDISCLOSED', description: 'Undisclosed'},
    {value: 'MEMBERS', description: 'Members'},
    {value: 'PUBLIC', description: 'Public'},
  ];

  answerTypes = [
    {value: 'TEXT', description: 'Text'},
    {value: 'NUMBER', description: 'Number'},
    {value: 'BOOLEAN', description: 'Boolean'},
    {value: 'LIST', description: 'List'},
  ];

  charts = [
    {value: 'visible-fee', description: 'Use of visible fee'},
    {value: 're-use-targets', description: 'Countries with re-use targets'},
    {value: 'e-waste-legislation', description: 'Categories in scope of e-waste legislation'},
    {value: 'weee-treatment-standard-mandatory', description: 'Is there a WEEE treatment standard mandatory?'},
    {value: 'country-retail-vs-municipalities-vs-other', description: 'Country % of WEEE collected from retail vs municipalities vs other'},
    {value: 'weee-system-retail-vs-municipalities-vs-other', description: 'WEEE System % of WEEE collected from retail vs municipalities vs other'},
    {value: 'collection-points-operated', description: 'Collection points serviced by WF members'},
    {value: 'employees-per-pro', description: 'Employees per PRO'},
    {value: 'recycling-facilities', description: 'Network of recycling facilities per PRO'},
    {value: 'members-adhered', description: 'Number of members adhered'},
    {value: 'systems-map', description: 'Number of systems in the country'},
  ];

  questionCreateForm = this.formBuilder.group({
    shortCode: ['', Validators.required],
    description: ['', Validators.required],
    label: [''],
    category: [1, Validators.required],
    yearly: [false],
    questionType: ['COUNTRY', Validators.required],
    atAGlanceInfo: [false],
    sharingLevelLocked: [false],
    sharingLevel: ['UNDISCLOSED', Validators.required],
    answerType: ['TEXT', Validators.required],
    maxLength: [0, Validators.required],
    minValue: [0, Validators.required],
    maxValue: [0, Validators.required],
    availableAnswers: [''],
    multiSelect: [false],
    mandatory: [false],
    comment: [''],
    chart: [],
  });

  get shortCodeControl(): FormControl {
    return this.questionCreateForm.get('shortCode') as FormControl;
  }
  get descriptionControl(): FormControl {
    return this.questionCreateForm.get('description') as FormControl;
  }
  get labelControl(): FormControl {
    return this.questionCreateForm.get('label') as FormControl;
  }
  get categoryControl(): FormControl {
    return this.questionCreateForm.get('category') as FormControl;
  }
  get yearlyControl(): FormControl {
    return this.questionCreateForm.get('yearly') as FormControl;
  }
  get questionTypeControl(): FormControl {
    return this.questionCreateForm.get('questionType') as FormControl;
  }
  get atAGlanceInfoControl(): FormControl {
    return this.questionCreateForm.get('atAGlanceInfo') as FormControl;
  }
  get sharingLevelLockedControl(): FormControl {
    return this.questionCreateForm.get('sharingLevelLocked') as FormControl;
  }
  get sharingLevelControl(): FormControl {
    return this.questionCreateForm.get('sharingLevel') as FormControl;
  }
  get answerTypeControl(): FormControl {
    return this.questionCreateForm.get('answerType') as FormControl;
  }
  get maxLengthControl(): FormControl {
    return this.questionCreateForm.get('maxLength') as FormControl;
  }
  get minValueControl(): FormControl {
    return this.questionCreateForm.get('minValue') as FormControl;
  }
  get maxValueControl(): FormControl {
    return this.questionCreateForm.get('maxValue') as FormControl;
  }
  get availableAnswersControl(): FormControl {
    return this.questionCreateForm.get('availableAnswers') as FormControl;
  }
  get multiSelectControl(): FormControl {
    return this.questionCreateForm.get('multiSelect') as FormControl;
  }
  get mandatoryControl(): FormControl {
    return this.questionCreateForm.get('mandatory') as FormControl;
  }
  get commentControl(): FormControl {
    return this.questionCreateForm.get('comment') as FormControl;
  }
  get chartControl(): FormControl {
    return this.questionCreateForm.get('chart') as FormControl;
  }

  @Output()
  createEvent: EventEmitter<void> = new EventEmitter<void>();

  constructor(private dialogRef: MatDialogRef<CreateQuestionsComponent>,
              @Inject(MAT_DIALOG_DATA) public data: Master[],
              private readonly formBuilder: FormBuilder,
              private readonly questionsService: QuestionsService) { }

  ngOnInit(): void {
    this.setMaxValueMinimumLimit();
  }

  sendCreationRequest(): void {
    const dto: Questions | null = this.getCreationDto();
    if (dto) {
      this.questionsService.createQuestion(dto).subscribe(
        () => {
          this.createEvent.emit();
          this.closeDialog();
        },
        () => {
          this.closeDialog();
        }
      );
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  setMaxValueMinimumLimit(): void {
    this.questionCreateForm.get('maxValue')?.setValidators(Validators.min(this.minValueControl.value));
    this.questionCreateForm.get('maxValue')?.updateValueAndValidity();
  }

  private getCreationDto(): Questions {
    let category: Master = {code: '', comment: '', description: '', disabled: false, id: 0};
    this.data.forEach(cat => {
      if (cat.id === this.categoryControl.value) {
        category = cat;
      }
    });
    let answerFormat = {};

    switch (this.answerTypeControl.value) {
      case 'TEXT':
        answerFormat = {MaxLength: this.maxLengthControl.value};
        break;
      case 'NUMBER':
        answerFormat = {Min: this.minValueControl.value, Max: this.maxValueControl.value};
        break;
      case 'BOOLEAN':
        answerFormat = {Value: false};
        break;
      case 'LIST':
        answerFormat = {AvailableAnswers: this.availableAnswersControl.value.split('\n')};
        break;
    }
    return {
      shortCode: this.shortCodeControl.value,
      description: this.descriptionControl.value,
      label: this.labelControl.value,
      category,
      yearly: this.yearlyControl.value,
      questionType: this.questionTypeControl.value,
      atAGlanceInfo: this.atAGlanceInfoControl.value,
      sharingLevel: this.sharingLevelControl.value,
      sharingLevelLocked: this.sharingLevelLockedControl.value,
      answerType: this.answerTypeControl.value,
      answerFormatString: JSON.stringify(answerFormat),
      mandatory: this.mandatoryControl.value,
      multiSelect: this.multiSelectControl.value,
      comment: this.commentControl.value,
      chart: this.chartControl.value
    };
  }

}
